import { __assign, __spreadArray } from "tslib";
import { differenceInDays, add } from 'date-fns';
export var chartLegendMap = {
    line: 'line',
    bar: 'rectangle',
    threshold: 'dashed'
};
export var TICK_LENGTH = 8;
export var TICK_MARGIN = 4;
export function computeDomainX(series, xScaleType) {
    if (xScaleType === 'categorical') {
        return series.reduce(function (acc, s) {
            if (s.series.type !== 'threshold') {
                s.series.data.forEach(function (_a) {
                    var x = _a.x;
                    if (acc.indexOf(x) === -1) {
                        acc.push(x);
                    }
                });
            }
            return acc;
        }, []);
    }
    return series.reduce(function (acc, curr) {
        if (curr.series.type === 'threshold') {
            return acc;
        }
        return curr.series.data.reduce(function (_a, _b) {
            var min = _a[0], max = _a[1];
            var x = _b.x;
            var newMin = min === undefined || x < min ? x : min;
            var newMax = max === undefined || max < x ? x : max;
            return [newMin, newMax];
        }, acc);
    }, []);
}
function find(arr, func) {
    for (var i = 0; i < arr.length; i++) {
        var found = func(arr[i]);
        if (found) {
            return arr[i];
        }
    }
    return null;
}
export function computeDomainY(series, stackedBars) {
    var _series = series;
    // For stacked bars, we need to accumulate all the bar series into a positive and a negative series
    if (stackedBars) {
        var _a = series.reduce(function (acc, curr) {
            if (curr.series.type === 'bar') {
                curr.series.data.forEach(function (_a) {
                    var x = _a.x, y = _a.y;
                    var data = y < 0 ? acc.negativeData : acc.positiveData;
                    var stackedDatum = find(data, function (el) { return matchesX(el.x, x); });
                    if (stackedDatum) {
                        stackedDatum.y += y;
                    }
                    else {
                        data.push({ x: x, y: y });
                    }
                    return acc;
                });
            }
            return acc;
        }, {
            positiveData: [],
            negativeData: []
        }), positiveData = _a.positiveData, negativeData = _a.negativeData;
        // Artificial series with the sum of all bars when stacked
        var stackedSeries = [
            {
                color: '',
                index: NaN,
                series: {
                    type: 'bar',
                    title: 'positive',
                    data: positiveData
                }
            },
            {
                color: '',
                index: NaN,
                series: {
                    type: 'bar',
                    title: 'negative',
                    data: negativeData
                }
            },
        ];
        // MixedLineBarChart can also contain other non-bar series,
        // so we replace all bars with the artificial bar series
        // Then proceed to compute range with it and the remaining (non-bar) series
        _series = __spreadArray(__spreadArray([], stackedSeries, true), _series.filter(function (s) { return s.series.type !== 'bar'; }), true);
    }
    return _series.reduce(function (acc, curr) {
        if (curr.series.type === 'threshold') {
            var min = acc[0], max = acc[1];
            var y = curr.series.y;
            var newMin = min === undefined || y < min ? y : min;
            var newMax = max === undefined || max < y ? y : max;
            return [newMin, newMax];
        }
        return curr.series.data.reduce(function (_a, _b) {
            var min = _a[0], max = _a[1];
            var y = _b.y;
            var newMin = min === undefined || y < min ? y : min;
            var newMax = max === undefined || max < y ? y : max;
            return [newMin, newMax];
        }, acc);
    }, [0, 0]);
}
export function createXTicks(scale, values) {
    if (scale.isNumeric()) {
        return scale.d3Scale.ticks(values);
    }
    else if (scale.isTime()) {
        var rawTicks = scale.d3Scale.ticks(values);
        var domain = scale.d3Scale.domain();
        return uniform(rawTicks, domain[domain.length - 1]);
    }
    else {
        return scale.d3Scale.domain();
    }
}
export function createYTicks(scale, values) {
    var ticks = scale.d3Scale.ticks(values);
    // The logarithmic scale sometimes produces a very large amount of (major and minor) ticks,
    // at which point we need to reduce them significantly for space.
    if (scale.scaleType === 'log' && ticks.length > 10) {
        return scale.d3Scale.ticks(3);
    }
    return ticks;
}
/**
 * Ensure uniformly-spaced ticks for 2-day intervals. d3-scale generates
 * ticks for even or odd numbers, which causes varying interval lengths
 * between months.
 */
function uniform(ticks, max) {
    if (ticks.length < 3 || !isMixedDayInterval(ticks)) {
        return ticks;
    }
    return createTwoDayInterval(ticks[0], max);
}
function isMixedDayInterval(ticks) {
    var oneDayInterval = false;
    var twoDayInterval = false;
    for (var i = 1; i < ticks.length; i++) {
        oneDayInterval = oneDayInterval || isDayInterval(ticks[i - 1], ticks[i], 1);
        twoDayInterval = twoDayInterval || isDayInterval(ticks[i - 1], ticks[i], 2);
    }
    return oneDayInterval && twoDayInterval;
}
function isDayInterval(a, b, difference) {
    if (difference === void 0) { difference = 1; }
    return Math.abs(differenceInDays(a, b)) === difference;
}
function createTwoDayInterval(start, max) {
    var result = [];
    var curr = start;
    while (curr < max) {
        result.push(curr);
        curr = add(curr, { days: 2 });
    }
    return result;
}
export function getOutlineRectForPoint(container, highlightedPointElement) {
    // Update focus outline by moving it over the currently highlighted segment
    var PADDING = 2;
    var containerRect = container.getBoundingClientRect();
    var pointRect = highlightedPointElement.getBoundingClientRect();
    return {
        x: "" + (pointRect.left - containerRect.left - PADDING),
        y: "" + (pointRect.top - containerRect.top - PADDING),
        width: "" + (pointRect.width + 2 * PADDING),
        height: "" + (pointRect.height + 2 * PADDING)
    };
}
// Starting from the given index, find the first x value in the x domain that has bar data attached to it.
export var nextValidDomainIndex = function (nextGroupIndex, barGroups, direction) {
    if (direction === void 0) { direction = 1; }
    var index = nextGroupIndex;
    if (index < 0 || index >= barGroups.length) {
        index = 0;
    }
    do {
        if (barGroups[index].isValid && barGroups[index].hasData) {
            return index;
        }
        index += direction;
        // Loop back to the beginning if necessary
        if (index >= barGroups.length) {
            index = 0;
        }
        else if (index < 0) {
            index = barGroups.length - 1;
        }
    } while (index !== nextGroupIndex);
    return 0;
};
/**
 * Returns a list of all bar (and mixed) groups in the series.
 */
export function getBarGroups(series, xScale) {
    if (xScale.scaleType !== 'categorical') {
        return [];
    }
    return xScale.domain.map(function (x) {
        var position = xScale.d3Scale(x);
        var isValid = typeof position !== 'undefined' && isFinite(position);
        return {
            x: x,
            isValid: isValid,
            hasData: series.some(function (_a) {
                var series = _a.series;
                if (series.type === 'threshold') {
                    // If there is a threshold series, every valid group will have a data point
                    return true;
                }
                return series.data.some(function (datum) { return matchesX(datum.x, x); });
            })
        };
    });
}
/**
 * Combine all line series into an array of scaled data points with the given scales.
 */
export function makeScaledSeries(series, xScale, yScale) {
    return series.reduce(function (acc, _a) {
        var series = _a.series, color = _a.color;
        if (series.type === 'line') {
            var offset_1 = xScale.isCategorical() ? Math.max(0, xScale.d3Scale.bandwidth() - 1) / 2 : 0;
            var scaledSerie = series.data.map(function (d) { return ({
                x: (xScale.d3Scale(d.x) || 0) + offset_1,
                y: yScale.d3Scale(d.y) || 0,
                datum: d,
                series: series,
                color: color
            }); });
            acc = acc.concat(scaledSerie);
        }
        else if (series.type === 'threshold') {
            acc = acc.concat([
                {
                    x: NaN,
                    y: yScale.d3Scale(series.y) || 0,
                    series: series,
                    color: color
                },
            ]);
        }
        return acc;
    }, []);
}
/**
 * Find the subset of series that are individually navigable with keyboard.
 * Lines and thresholds are navigated individiually, while bar series are grouped as one.
 */
export function findNavigableSeries(series) {
    var navigableSeries = [];
    var navigableBarSeriesIndex = -1;
    series.forEach(function (internalSeries) {
        if (internalSeries.series.type === 'bar') {
            // Only include the first bar series because all bar series are handled as one
            if (navigableBarSeriesIndex === -1) {
                navigableBarSeriesIndex = navigableSeries.length;
                navigableSeries.push(internalSeries.series);
            }
        }
        else {
            navigableSeries.push(internalSeries.series);
        }
    });
    return { navigableSeries: navigableSeries, navigableBarSeriesIndex: navigableBarSeriesIndex };
}
/**
 * Checks if two x values are equal.
 * With a special treat for Date values which need to be converted to numbers first.
 */
export var matchesX = function (x1, x2) {
    if (x1 instanceof Date && x2 instanceof Date) {
        return x1.getTime() === x2.getTime();
    }
    return x1 === x2;
};
export function focusSVGElement(target) {
    if (target.focus) {
        target.focus();
    }
    else {
        // Deferring in a timeout because IE11 is not updating `tabIndex` and `focusable` attributes on time for some reason
        setTimeout(function () {
            // IE11 does not implement .focus on SVG elements, but the HTMLElement version works.
            window.HTMLElement.prototype.focus.call(target);
        }, 0);
    }
}
/**
 * Calculates list of offset maps from all data by accumulating each value
 */
export function calculateOffsetMaps(data) {
    return data.reduce(function (acc, curr, idx) {
        // First series receives empty offsets map
        if (idx === 0) {
            acc.push({ positiveOffsets: {}, negativeOffsets: {} });
        }
        var lastMap = acc[idx];
        var map = lastMap
            ? { positiveOffsets: __assign({}, lastMap.positiveOffsets), negativeOffsets: __assign({}, lastMap.negativeOffsets) }
            : { positiveOffsets: {}, negativeOffsets: {} };
        curr.forEach(function (_a) {
            var x = _a.x, y = _a.y;
            var key = getKeyValue(x);
            if (y < 0) {
                var lastValue = (lastMap === null || lastMap === void 0 ? void 0 : lastMap.negativeOffsets[key]) || 0;
                map.negativeOffsets[key] = lastValue + y;
            }
            else {
                var lastValue = (lastMap === null || lastMap === void 0 ? void 0 : lastMap.positiveOffsets[key]) || 0;
                map.positiveOffsets[key] = lastValue + y;
            }
        });
        // Ignore last value for map but still run it for logging
        if (idx < data.length - 1) {
            acc.push(map);
        }
        return acc;
    }, []);
}
/**
 * Returns string or number value for ChartDataTypes key
 */
export var getKeyValue = function (key) { return (key instanceof Date ? key.getTime() : key); };
//# sourceMappingURL=utils.js.map